import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListSubheader,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Save,
  Business,
  HelpOutline,
  InfoOutlined,
  Dialpad,
  GraphicEq,
  Schedule,
  QueueMusic,
  Subscriptions as SubscriptionsIcon,
  ExpandMore,
  VpnKey,
  ShieldOutlined,
} from '@mui/icons-material';
import DetailHeader from '../components/DetailHeader';
import ButtonTabs, { type ButtonTab } from '../components/ButtonTabs';
import { parseNotificationEmails } from '../utils/notificationEmails';
import { useTranslation } from 'react-i18next';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs, { Dayjs } from 'dayjs';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import type { DayOperatingHours, OperatingHours, Tenant } from '../types';
import usePageTitle from '../hooks/usePageTitle';
import { usePermissions } from '../contexts/PermissionContext';
import useTenantWebSocket from '../hooks/useTenantWebSocket';
import SubscriptionUsagePeriodsTable from '../components/SubscriptionUsagePeriodsTable';
import WaveformPlayerField from '../components/WaveformPlayerField';
import PublicApiCredentialsManager from '../components/PublicApiCredentialsManager';
import { useNumberFormat } from '../hooks/useNumberFormat';
import type {
  Addon,
  MinutePack,
  MinutePackCatalog,
  SubscriptionAddon,
  SubscriptionPlan,
  SubscriptionPlanType,
  SubscriptionUsagePeriodSummary,
} from '../types';
import {
  activateTenantAddonAdmin,
  assignTenantSubscriptionAdmin,
  cancelTenantAddonAdmin,
  createTenantMinutePackAdmin,
  fetchAddonsAdmin,
  fetchMinutePackCatalog,
  fetchPlansAdmin,
  fetchSubscriptionUsagePeriods,
  fetchTenantAddonsAdmin,
  fetchTenantMinutePacksAdmin,
  fetchTenantSubscriptionsAdmin,
  type TenantSubscriptionAdminItem,
} from '../services/subscriptions';
import { formatDurationValue } from '../utils/duration';

const DAYS_OF_WEEK = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
type OperatingDay = (typeof DAYS_OF_WEEK)[number];

const DEFAULT_OPERATING_HOURS: OperatingHours = {
  mon: { enabled: true, allDay: false, start: '09:00', end: '20:00' },
  tue: { enabled: true, allDay: false, start: '09:00', end: '20:00' },
  wed: { enabled: true, allDay: false, start: '09:00', end: '20:00' },
  thu: { enabled: true, allDay: false, start: '09:00', end: '20:00' },
  fri: { enabled: true, allDay: false, start: '09:00', end: '20:00' },
  sat: { enabled: true, allDay: false, start: '09:00', end: '14:00' },
  sun: { enabled: false, allDay: false, start: null, end: null },
};

const TENANT_FORM_TABS = {
  GENERAL: 'general',
  REVERSE_IVR: 'reverse_ivr',
  AUDIO_TUNING: 'audio_tuning',
  OPERATING_HOURS: 'operating_hours',
  MUSIC_ON_HOLD: 'music_on_hold',
  SUBSCRIPTIONS: 'subscriptions',
  PUBLIC_API: 'public_api',
} as const;

type TenantFormTab = (typeof TENANT_FORM_TABS)[keyof typeof TENANT_FORM_TABS];

interface TenantFormData {
  name: string;
  email: string;
  notificationEmails: string[];
  isActive: boolean;
  ivrMaxAudioSeconds: number;
  ivrMaxCallDurationSeconds: number;
  ivrMaxPostAudioWaitSeconds: number;
  pstnDenoiseRxEnabled: boolean;
  pstnRxVolumeGain: number;
  operatingHours: OperatingHours;
  autoOperatorReadinessGuardEnabled: boolean;
  diagnosticRecordingTracksEnabled: boolean;
}

interface TenantMusicStatus {
  queueWaitMusicFileId: string | null;
  queueWaitMusicClass: string | null;
  queueWaitMusicPrepared: boolean;
  queueWaitMusicError: string | null;
  queueWaitMusicGeneratedAt: string | null;
  agentHoldMusicFileId: string | null;
  agentHoldMusicClass: string | null;
  agentHoldMusicPrepared: boolean;
  agentHoldMusicError: string | null;
  agentHoldMusicGeneratedAt: string | null;
}

interface TenantMusicMedia {
  id: string;
  originalFilename: string;
  mimeType: string;
  fileSize: number;
  downloadUrl: string | null;
}

const initialMusicStatus: TenantMusicStatus = {
  queueWaitMusicFileId: null,
  queueWaitMusicClass: null,
  queueWaitMusicPrepared: false,
  queueWaitMusicError: null,
  queueWaitMusicGeneratedAt: null,
  agentHoldMusicFileId: null,
  agentHoldMusicClass: null,
  agentHoldMusicPrepared: false,
  agentHoldMusicError: null,
  agentHoldMusicGeneratedAt: null,
};

const initialFormData: TenantFormData = {
  name: '',
  email: '',
  notificationEmails: [],
  isActive: true,
  ivrMaxAudioSeconds: 27,
  ivrMaxCallDurationSeconds: 29,
  ivrMaxPostAudioWaitSeconds: 2,
  pstnDenoiseRxEnabled: false,
  pstnRxVolumeGain: 0,
  operatingHours: DEFAULT_OPERATING_HOURS,
  autoOperatorReadinessGuardEnabled: true,
  diagnosticRecordingTracksEnabled: false,
};

/**
 * Normalizes the tenant operating hours payload from API into a complete week shape.
 * Missing days or fields are filled with project defaults to keep form rendering stable.
 */
const normalizeOperatingHours = (
  source?: Partial<Record<OperatingDay, DayOperatingHours>>
): OperatingHours => {
  const normalized: OperatingHours = { ...DEFAULT_OPERATING_HOURS };
  if (!source) {
    return normalized;
  }

  DAYS_OF_WEEK.forEach((day) => {
    const dayConfig = source[day];
    if (!dayConfig) {
      return;
    }
    const enabled = Boolean(dayConfig.enabled);
    const allDay = enabled && Boolean(dayConfig.allDay ?? (dayConfig as { all_day?: boolean }).all_day);
    normalized[day] = {
      enabled,
      allDay,
      start: enabled && !allDay ? dayConfig.start ?? null : null,
      end: enabled && !allDay ? dayConfig.end ?? null : null,
    };
  });

  return normalized;
};

/**
 * Parse optional currency-amount inputs used in tenant billing actions.
 * Returns `undefined` for empty or invalid values so callers can fall back to catalog pricing.
 */
const parseOptionalPriceAmount = (rawValue: string | undefined): number | undefined => {
  const normalized = (rawValue || '').trim();
  if (!normalized) {
    return undefined;
  }
  const numericValue = Number(normalized);
  if (!Number.isFinite(numericValue)) {
    return undefined;
  }
  return numericValue;
};

export default function TenantFormPage() {
  const { t } = useTranslation(['pages', 'common']);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);
  const { hasPermission } = usePermissions();
  const { user } = useAuth();
  const { formatCurrency } = useNumberFormat();
  const canEditNotificationEmails = hasPermission('tenants_update');
  const canReadPublicApiCredentials = hasPermission('public_api_credentials_read');

  const [formData, setFormData] = useState<TenantFormData>(initialFormData);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [notificationEmailsInput, setNotificationEmailsInput] = useState('');
  const [notificationEmailsError, setNotificationEmailsError] = useState<string | null>(null);
  const [ivrLimitsError, setIvrLimitsError] = useState<string | null>(null);
  const [audioTuningError, setAudioTuningError] = useState<string | null>(null);
  const [operatingHoursError, setOperatingHoursError] = useState<string | null>(null);
  const [musicStatus, setMusicStatus] = useState<TenantMusicStatus>(initialMusicStatus);
  const [musicMedia, setMusicMedia] = useState<{
    queueWait: TenantMusicMedia | null;
    agentHold: TenantMusicMedia | null;
  }>({
    queueWait: null,
    agentHold: null,
  });
  const [uploadingMusicSlot, setUploadingMusicSlot] = useState<'queueWait' | 'agentHold' | null>(null);
  const [musicUploadProgress, setMusicUploadProgress] = useState<{
    queueWait: number | null;
    agentHold: number | null;
  }>({
    queueWait: null,
    agentHold: null,
  });
  const [activeTab, setActiveTab] = useState<TenantFormTab>(TENANT_FORM_TABS.GENERAL);
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [minutePackCatalog, setMinutePackCatalog] = useState<MinutePackCatalog[]>([]);
  const [availableAddons, setAvailableAddons] = useState<Addon[]>([]);
  const [tenantSubscriptions, setTenantSubscriptions] = useState<TenantSubscriptionAdminItem[]>([]);
  const [tenantMinutePacks, setTenantMinutePacks] = useState<MinutePack[]>([]);
  const [tenantAddons, setTenantAddons] = useState<SubscriptionAddon[]>([]);
  const [tenantUsagePeriods, setTenantUsagePeriods] = useState<SubscriptionUsagePeriodSummary[]>([]);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingSaving, setBillingSaving] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [selectedPlanPriceOverride, setSelectedPlanPriceOverride] = useState('');
  const [selectedPackBySubscription, setSelectedPackBySubscription] = useState<Record<string, string>>({});
  const [packPriceAmountBySubscription, setPackPriceAmountBySubscription] = useState<Record<string, string>>({});
  const [selectedAddonBySubscription, setSelectedAddonBySubscription] = useState<Record<string, string>>({});
  const [addonPriceAmountBySubscription, setAddonPriceAmountBySubscription] = useState<Record<string, string>>({});
  const [addonQuantityBySubscription, setAddonQuantityBySubscription] = useState<Record<string, string>>({});
  const [addonAutoRenewBySubscription, setAddonAutoRenewBySubscription] = useState<Record<string, boolean>>({});
  const descriptor = formData.name || tenantId || undefined;
  usePageTitle(t('pages:tenants.title'), descriptor);

  const fetchTenant = useCallback(async (tenantId: string) => {
    try {
      const response = await api.get(`/api/tenants/${tenantId}/`);
      const tenant: Tenant = response.data;
      setFormData({
        name: tenant.name,
        email: tenant.email,
        notificationEmails: tenant.notificationEmails || [],
        isActive: tenant.isActive,
        ivrMaxAudioSeconds: tenant.ivrMaxAudioSeconds ?? 27,
        ivrMaxCallDurationSeconds: tenant.ivrMaxCallDurationSeconds ?? 29,
        ivrMaxPostAudioWaitSeconds: tenant.ivrMaxPostAudioWaitSeconds ?? 2,
        pstnDenoiseRxEnabled: Boolean(tenant.pstnDenoiseRxEnabled),
        pstnRxVolumeGain: tenant.pstnRxVolumeGain ?? 0,
        operatingHours: normalizeOperatingHours(tenant.operatingHours),
        autoOperatorReadinessGuardEnabled: tenant.autoOperatorReadinessGuardEnabled ?? true,
        diagnosticRecordingTracksEnabled: tenant.diagnosticRecordingTracksEnabled ?? false,
      });
      setTenantId(tenant.id);
      setMusicStatus({
        queueWaitMusicFileId: tenant.queueWaitMusicFileId ?? null,
        queueWaitMusicClass: tenant.queueWaitMusicClass ?? null,
        queueWaitMusicPrepared: Boolean(tenant.queueWaitMusicPrepared),
        queueWaitMusicError: tenant.queueWaitMusicError ?? null,
        queueWaitMusicGeneratedAt: tenant.queueWaitMusicGeneratedAt ?? null,
        agentHoldMusicFileId: tenant.agentHoldMusicFileId ?? null,
        agentHoldMusicClass: tenant.agentHoldMusicClass ?? null,
        agentHoldMusicPrepared: Boolean(tenant.agentHoldMusicPrepared),
        agentHoldMusicError: tenant.agentHoldMusicError ?? null,
        agentHoldMusicGeneratedAt: tenant.agentHoldMusicGeneratedAt ?? null,
      });
      const joinedEmails = (tenant.notificationEmails || []).join(', ');
      setNotificationEmailsInput(joinedEmails);
    } catch (err) {
      console.error('Error loading tenant:', err);
      setError(t('pages:tenants.errors.loading') || 'Failed to load tenant');
    } finally {
      setLoading(false);
    }
  }, [t]);

  /**
   * Loads tenant-scoped billing resources used by the subscriptions accordion.
   */
  const loadTenantBillingData = useCallback(async (targetTenantId: string) => {
    setBillingLoading(true);
    try {
      const [plans, catalogPacks, addons, subscriptions, packs, tenantAddonsResponse, usagePeriods] = await Promise.all([
        fetchPlansAdmin(),
        fetchMinutePackCatalog(),
        fetchAddonsAdmin(),
        fetchTenantSubscriptionsAdmin(targetTenantId),
        fetchTenantMinutePacksAdmin(targetTenantId),
        fetchTenantAddonsAdmin(targetTenantId),
        fetchSubscriptionUsagePeriods({ tenantId: targetTenantId, ordering: '-period_start' }),
      ]);
      setSubscriptionPlans(plans);
      setMinutePackCatalog(catalogPacks.filter((pack) => pack.isActive));
      setAvailableAddons(addons.filter((addon) => addon.isActive));
      setTenantSubscriptions(subscriptions);
      setTenantMinutePacks(packs);
      setTenantAddons(tenantAddonsResponse);
      setTenantUsagePeriods(usagePeriods);
    } catch (err) {
      console.error('Error loading tenant billing data:', err);
      setError(t('pages:tenants.billing.errors.load'));
    } finally {
      setBillingLoading(false);
    }
  }, [t]);

  /**
   * Loads media metadata and presigned playback URL for a tenant MOH slot.
   */
  const fetchMusicMedia = useCallback(async (fileId: string | null): Promise<TenantMusicMedia | null> => {
    if (!fileId) {
      return null;
    }
    try {
      const response = await api.get(`/api/media/${fileId}/`);
      return {
        id: response.data?.id,
        originalFilename: response.data?.originalFilename || response.data?.original_filename || '-',
        mimeType: response.data?.mimeType || response.data?.mime_type || 'application/octet-stream',
        fileSize: response.data?.fileSize || response.data?.file_size || 0,
        downloadUrl: response.data?.downloadUrl || response.data?.download_url || null,
      };
    } catch (err) {
      console.error('Error loading media metadata:', err);
      return null;
    }
  }, []);

  useEffect(() => {
    if (isEditing && id) {
      fetchTenant(id);
    } else {
      setLoading(false);
    }
  }, [id, isEditing, fetchTenant]);

  useEffect(() => {
    if (!isEditing || !id) {
      setTenantSubscriptions([]);
      setTenantMinutePacks([]);
      setTenantAddons([]);
      setTenantUsagePeriods([]);
      setMinutePackCatalog([]);
      setSelectedPackBySubscription({});
      setPackPriceAmountBySubscription({});
      setSelectedAddonBySubscription({});
      setAddonPriceAmountBySubscription({});
      setAddonQuantityBySubscription({});
      setAddonAutoRenewBySubscription({});
      return;
    }
    void loadTenantBillingData(id);
  }, [id, isEditing, loadTenantBillingData]);

  useEffect(() => {
    let cancelled = false;
    const loadMusicMedia = async () => {
      const [queueWait, agentHold] = await Promise.all([
        fetchMusicMedia(musicStatus.queueWaitMusicFileId),
        fetchMusicMedia(musicStatus.agentHoldMusicFileId),
      ]);
      if (!cancelled) {
        setMusicMedia({ queueWait, agentHold });
      }
    };
    if (!isEditing) {
      setMusicMedia({ queueWait: null, agentHold: null });
      return;
    }
    void loadMusicMedia();
    return () => {
      cancelled = true;
    };
  }, [
    isEditing,
    musicStatus.queueWaitMusicFileId,
    musicStatus.agentHoldMusicFileId,
    fetchMusicMedia,
  ]);

  const handleTenantRealtimeEvent = useCallback(() => {
    if (id && !saving) {
      void fetchTenant(id);
    }
  }, [id, saving, fetchTenant]);

  useTenantWebSocket(isEditing ? id : null, {
    enabled: Boolean(isEditing && id),
    onEvent: handleTenantRealtimeEvent,
  });

  const validateNotificationEmails = (value: string) => {
    const emails = parseNotificationEmails(value);
    if (emails.length === 0) {
      return { emails: [], error: null };
    }
    const invalidEmails = emails.filter((email) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
    if (invalidEmails.length > 0) {
      return {
        emails,
        error: t('pages:tenants.notificationEmailsInvalid', { count: invalidEmails.length }),
      };
    }
    return { emails, error: null };
  };

  const handleNotificationEmailsChange = (value: string) => {
    setNotificationEmailsInput(value);
    const { emails, error: validationError } = validateNotificationEmails(value);
    setNotificationEmailsError(validationError);
    setFormData((prev) => ({ ...prev, notificationEmails: emails }));
  };

  const validateIvrLimits = (data: TenantFormData): string | null => {
    if (data.ivrMaxAudioSeconds <= 0 || data.ivrMaxCallDurationSeconds <= 0) {
      return t('pages:tenants.ivrLimitsMustBePositive');
    }
    if (data.ivrMaxPostAudioWaitSeconds < 0) {
      return t('pages:tenants.ivrPostAudioWaitMustBeNonNegative');
    }
    if (data.ivrMaxAudioSeconds >= data.ivrMaxCallDurationSeconds) {
      return t('pages:tenants.ivrLimitsInvalid');
    }
    return null;
  };

  const validateAudioTuning = (data: TenantFormData): string | null => {
    if (data.pstnRxVolumeGain < -20 || data.pstnRxVolumeGain > 20) {
      return t('pages:tenants.pstnRxVolumeGainOutOfRange');
    }
    return null;
  };

  const getDayLabel = (day: OperatingDay): string => {
    const labels: Record<OperatingDay, string> = {
      mon: t('common:days.monday'),
      tue: t('common:days.tuesday'),
      wed: t('common:days.wednesday'),
      thu: t('common:days.thursday'),
      fri: t('common:days.friday'),
      sat: t('common:days.saturday'),
      sun: t('common:days.sunday'),
    };
    return labels[day];
  };

  /**
   * Validates business constraints for operating hours before sending the payload.
   * Enabled non-24h days must have start/end and start time must be earlier than end time.
   */
  const validateOperatingHours = (hours: OperatingHours): string | null => {
    for (const day of DAYS_OF_WEEK) {
      const config = hours[day];
      if (!config.enabled) {
        continue;
      }
      if (config.allDay) {
        continue;
      }
      if (!config.start || !config.end) {
        return t('pages:tenants.operatingHoursIncomplete', { day: getDayLabel(day) });
      }
      if (config.start >= config.end) {
        return t('pages:tenants.operatingHoursInvalidRange', { day: getDayLabel(day) });
      }
    }
    return null;
  };

  const handleIvrLimitChange = (
    field: 'ivrMaxAudioSeconds' | 'ivrMaxCallDurationSeconds' | 'ivrMaxPostAudioWaitSeconds',
    value: string,
  ) => {
    const numericValue = Number(value);
    setFormData((prev) => ({
      ...prev,
      [field]: Number.isFinite(numericValue) ? numericValue : 0,
    }));
    setIvrLimitsError(null);
  };

  const handleOperatingHourToggle = (day: OperatingDay) => {
    setFormData((prev) => ({
      ...prev,
      operatingHours: {
        ...prev.operatingHours,
        [day]: {
          ...prev.operatingHours[day],
          enabled: !prev.operatingHours[day].enabled,
          allDay: prev.operatingHours[day].enabled ? false : prev.operatingHours[day].allDay,
          start: prev.operatingHours[day].enabled
            ? null
            : prev.operatingHours[day].start ?? DEFAULT_OPERATING_HOURS[day].start,
          end: prev.operatingHours[day].enabled
            ? null
            : prev.operatingHours[day].end ?? DEFAULT_OPERATING_HOURS[day].end,
        },
      },
    }));
    setOperatingHoursError(null);
  };

  const handleOperatingHourAllDayToggle = (day: OperatingDay) => {
    setFormData((prev) => {
      const current = prev.operatingHours[day];
      const nextAllDay = !current.allDay;
      return {
        ...prev,
        operatingHours: {
          ...prev.operatingHours,
          [day]: {
            ...current,
            allDay: nextAllDay,
            start: nextAllDay ? null : current.start ?? DEFAULT_OPERATING_HOURS[day].start,
            end: nextAllDay ? null : current.end ?? DEFAULT_OPERATING_HOURS[day].end,
          },
        },
      };
    });
    setOperatingHoursError(null);
  };

  const handleOperatingHourTimeChange = (day: OperatingDay, field: 'start' | 'end', value: string) => {
    setFormData((prev) => ({
      ...prev,
      operatingHours: {
        ...prev.operatingHours,
        [day]: {
          ...prev.operatingHours[day],
          [field]: value || null,
        },
      },
    }));
    setOperatingHoursError(null);
  };

  const handleSubmit = async (event?: FormEvent) => {
    event?.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const { error: validationError } = validateNotificationEmails(notificationEmailsInput);
      if (validationError) {
        setNotificationEmailsError(validationError);
        setSaving(false);
        return;
      }
      const ivrValidationError = validateIvrLimits(formData);
      if (ivrValidationError) {
        setIvrLimitsError(ivrValidationError);
        setActiveTab(TENANT_FORM_TABS.REVERSE_IVR);
        setSaving(false);
        return;
      }
      const audioTuningValidationError = validateAudioTuning(formData);
      if (audioTuningValidationError) {
        setAudioTuningError(audioTuningValidationError);
        setActiveTab(TENANT_FORM_TABS.AUDIO_TUNING);
        setSaving(false);
        return;
      }
      const operatingValidationError = validateOperatingHours(formData.operatingHours);
      if (operatingValidationError) {
        setOperatingHoursError(operatingValidationError);
        setActiveTab(TENANT_FORM_TABS.OPERATING_HOURS);
        setSaving(false);
        return;
      }
      const { diagnosticRecordingTracksEnabled, ...customerFields } = formData;
      const payload = user?.isSuperuser === true ? { ...customerFields, diagnosticRecordingTracksEnabled } : customerFields;
      if (isEditing && id) {
        await api.patch(`/api/tenants/${id}/`, payload);
        await fetchTenant(id);
        setSuccess(t('pages:tenants.saveSuccess'));
      } else {
        const response = await api.post('/api/tenants/', payload);
        const createdTenantId = response.data?.id;
        if (createdTenantId) {
          navigate(`/tenants/${createdTenantId}`);
          return;
        }
        setSuccess(t('pages:tenants.saveSuccess'));
      }
    } catch (err: unknown) {
      console.error('Error saving tenant:', err);
      const maybeFieldError = (err as { response?: { data?: Record<string, unknown> } })?.response?.data;
      const ivrFieldError = (
        maybeFieldError?.ivrMaxAudioSeconds
        || maybeFieldError?.ivr_max_audio_seconds
        || maybeFieldError?.ivrMaxCallDurationSeconds
        || maybeFieldError?.ivr_max_call_duration_seconds
        || maybeFieldError?.ivrMaxPostAudioWaitSeconds
        || maybeFieldError?.ivr_max_post_audio_wait_seconds
      );
      if (typeof ivrFieldError === 'string') {
        setIvrLimitsError(ivrFieldError);
        setActiveTab(TENANT_FORM_TABS.REVERSE_IVR);
      } else if (Array.isArray(ivrFieldError) && ivrFieldError.length > 0 && typeof ivrFieldError[0] === 'string') {
        setIvrLimitsError(ivrFieldError[0]);
        setActiveTab(TENANT_FORM_TABS.REVERSE_IVR);
      }
      const audioTuningFieldError = maybeFieldError?.pstnRxVolumeGain || maybeFieldError?.pstn_rx_volume_gain;
      if (typeof audioTuningFieldError === 'string') {
        setAudioTuningError(audioTuningFieldError);
        setActiveTab(TENANT_FORM_TABS.AUDIO_TUNING);
      } else if (
        Array.isArray(audioTuningFieldError) &&
        audioTuningFieldError.length > 0 &&
        typeof audioTuningFieldError[0] === 'string'
      ) {
        setAudioTuningError(audioTuningFieldError[0]);
        setActiveTab(TENANT_FORM_TABS.AUDIO_TUNING);
      }
      const operatingHoursFieldError = maybeFieldError?.operatingHours || maybeFieldError?.operating_hours;
      if (typeof operatingHoursFieldError === 'string') {
        setOperatingHoursError(operatingHoursFieldError);
        setActiveTab(TENANT_FORM_TABS.OPERATING_HOURS);
      } else if (
        Array.isArray(operatingHoursFieldError) &&
        operatingHoursFieldError.length > 0 &&
        typeof operatingHoursFieldError[0] === 'string'
      ) {
        setOperatingHoursError(operatingHoursFieldError[0]);
        setActiveTab(TENANT_FORM_TABS.OPERATING_HOURS);
      } else if (operatingHoursFieldError) {
        setOperatingHoursError(t('pages:tenants.operatingHoursInvalid'));
        setActiveTab(TENANT_FORM_TABS.OPERATING_HOURS);
      }
      setError(t('pages:tenants.errors.saving') || 'Failed to save tenant');
    } finally {
      setSaving(false);
    }
  };

  const breadcrumbs = [
    { label: t('common:menu.tenants'), to: '/tenants' },
    { label: isEditing ? t('pages:tenants.edit') : t('pages:tenants.new') },
  ];

  const getMusicStatusTone = (prepared: boolean, error: string | null): 'success' | 'error' | 'warning' => {
    if (error) {
      return 'error';
    }
    if (prepared) {
      return 'success';
    }
    return 'warning';
  };

  const getMusicStatusLabel = (slot: 'queueWait' | 'agentHold'): string => {
    const isPrepared = slot === 'queueWait' ? musicStatus.queueWaitMusicPrepared : musicStatus.agentHoldMusicPrepared;
    const error = slot === 'queueWait' ? musicStatus.queueWaitMusicError : musicStatus.agentHoldMusicError;
    if (error) {
      return t('pages:tenants.musicOnHold.status.error');
    }
    if (isPrepared) {
      return t('pages:tenants.musicOnHold.status.ready');
    }
    return t('pages:tenants.musicOnHold.status.preparing');
  };

  const hasMusicFile = (slot: 'queueWait' | 'agentHold'): boolean =>
    slot === 'queueWait'
      ? Boolean(musicStatus.queueWaitMusicFileId)
      : Boolean(musicStatus.agentHoldMusicFileId);

  const formatFileSize = (bytes: number): string => {
    if (!Number.isFinite(bytes) || bytes <= 0) {
      return '0 B';
    }
    const units = ['B', 'KB', 'MB', 'GB'];
    const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const size = bytes / 1024 ** exponent;
    return `${size.toFixed(exponent === 0 ? 0 : 2)} ${units[exponent]}`;
  };

  /**
   * Uploads a raw audio file to storage and binds it to a tenant MOH slot.
   * Backend keeps the original file in Wasabi and triggers asynchronous MOH preparation.
   */
  const handleMusicFileUpload = async (slot: 'queueWait' | 'agentHold', file: File | null) => {
    if (!file || !id) {
      return;
    }
    setUploadingMusicSlot(slot);
    setMusicUploadProgress((prev) => ({ ...prev, [slot]: 0 }));
    setError(null);
    setSuccess(null);
    try {
      const uploadPayload = new FormData();
      uploadPayload.append('file', file);
      uploadPayload.append('file_type', 'other');
      uploadPayload.append(
        'metadata',
        JSON.stringify({
          category: 'tenant_music_on_hold',
          slot,
        })
      );
      const uploadResponse = await api.post('/api/media/upload/', uploadPayload, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (event) => {
          if (!event.total || event.total <= 0) {
            setMusicUploadProgress((prev) => ({ ...prev, [slot]: null }));
            return;
          }
          const progressValue = (event.loaded / event.total) * 100;
          setMusicUploadProgress((prev) => ({ ...prev, [slot]: progressValue }));
        },
      });
      const uploadedMediaId = uploadResponse.data?.id;
      if (!uploadedMediaId) {
        throw new Error('Media upload did not return id');
      }
      const patchPayload =
        slot === 'queueWait'
          ? { queueWaitMusicFileId: uploadedMediaId }
          : { agentHoldMusicFileId: uploadedMediaId };
      await api.patch(`/api/tenants/${id}/`, patchPayload);
      await fetchTenant(id);
      setSuccess(t('pages:tenants.musicOnHold.uploadSuccess'));
    } catch (err) {
      console.error('Error uploading tenant music file:', err);
      setError(t('pages:tenants.musicOnHold.errors.uploadFailed'));
    } finally {
      setUploadingMusicSlot(null);
      setMusicUploadProgress((prev) => ({ ...prev, [slot]: null }));
    }
  };

  /**
   * Clears the selected media binding for a MOH slot in the tenant.
   */
  const handleMusicFileRemove = async (slot: 'queueWait' | 'agentHold') => {
    if (!id) {
      return;
    }
    setUploadingMusicSlot(slot);
    setMusicUploadProgress((prev) => ({ ...prev, [slot]: null }));
    setError(null);
    setSuccess(null);
    try {
      const patchPayload =
        slot === 'queueWait'
          ? { queueWaitMusicFileId: null }
          : { agentHoldMusicFileId: null };
      await api.patch(`/api/tenants/${id}/`, patchPayload);
      await fetchTenant(id);
      setSuccess(t('pages:tenants.musicOnHold.removeSuccess'));
    } catch (err) {
      console.error('Error removing tenant music file:', err);
      setError(t('pages:tenants.musicOnHold.errors.removeFailed'));
    } finally {
      setUploadingMusicSlot(null);
      setMusicUploadProgress((prev) => ({ ...prev, [slot]: null }));
    }
  };

  const handleAssignSubscription = async () => {
    if (!id || !selectedPlanId) {
      return;
    }
    setBillingSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const parsedPlanOverride = parseOptionalPriceAmount(selectedPlanPriceOverride);
      await assignTenantSubscriptionAdmin({
        tenantId: id,
        planId: selectedPlanId,
        replaceActive: true,
        planPriceAmountOverride: parsedPlanOverride ?? null,
      });
      await loadTenantBillingData(id);
      setSuccess(t('pages:tenants.billing.subscriptions.assignSuccess'));
      setSelectedPlanId('');
      setSelectedPlanPriceOverride('');
    } catch (err) {
      console.error('Error assigning tenant subscription:', err);
      setError(t('pages:tenants.billing.errors.assignSubscription'));
    } finally {
      setBillingSaving(false);
    }
  };

  const handleAttachPack = async (subscription: TenantSubscriptionAdminItem) => {
    if (!id) {
      return;
    }
    const selectedPackId = selectedPackBySubscription[subscription.id];
    if (!selectedPackId) {
      return;
    }

    setBillingSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await createTenantMinutePackAdmin(id, {
        packId: selectedPackId,
        subscriptionType: subscription.planType,
        priceAmount: parseOptionalPriceAmount(packPriceAmountBySubscription[subscription.id]),
      });
      await loadTenantBillingData(id);
      setSuccess(t('pages:tenants.billing.packs.attachSuccess'));
      setSelectedPackBySubscription((prev) => ({ ...prev, [subscription.id]: '' }));
      setPackPriceAmountBySubscription((prev) => ({ ...prev, [subscription.id]: '' }));
    } catch (err) {
      console.error('Error attaching tenant minute pack:', err);
      setError(t('pages:tenants.billing.errors.createPack'));
    } finally {
      setBillingSaving(false);
    }
  };

  const handleAttachAddon = async (subscription: TenantSubscriptionAdminItem) => {
    if (!id) {
      return;
    }
    const selectedAddonId = selectedAddonBySubscription[subscription.id];
    if (!selectedAddonId) {
      return;
    }
    const quantityValue = addonQuantityBySubscription[subscription.id] || '1';
    const quantity = Number(quantityValue);
    if (!Number.isFinite(quantity) || quantity <= 0) {
      setError(t('pages:tenants.billing.addons.invalidQuantity'));
      return;
    }

    setBillingSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await activateTenantAddonAdmin(id, {
        addonId: selectedAddonId,
        subscriptionId: subscription.id,
        subscriptionType: subscription.planType,
        quantity,
        priceAmount: parseOptionalPriceAmount(addonPriceAmountBySubscription[subscription.id]),
        autoRenew: addonAutoRenewBySubscription[subscription.id] ?? true,
      });
      await loadTenantBillingData(id);
      setSuccess(t('pages:tenants.billing.addons.activateSuccess'));
      setSelectedAddonBySubscription((prev) => ({ ...prev, [subscription.id]: '' }));
      setAddonPriceAmountBySubscription((prev) => ({ ...prev, [subscription.id]: '' }));
      setAddonQuantityBySubscription((prev) => ({ ...prev, [subscription.id]: '1' }));
      setAddonAutoRenewBySubscription((prev) => ({ ...prev, [subscription.id]: true }));
    } catch (err) {
      console.error('Error activating tenant addon:', err);
      setError(t('pages:tenants.billing.errors.activateAddon'));
    } finally {
      setBillingSaving(false);
    }
  };

  const handleCancelAddon = async (subscriptionAddonId: string) => {
    if (!id) {
      return;
    }
    setBillingSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await cancelTenantAddonAdmin(id, subscriptionAddonId);
      await loadTenantBillingData(id);
      setSuccess(t('pages:tenants.billing.addons.cancelSuccess'));
    } catch (err) {
      console.error('Error canceling tenant addon:', err);
      setError(t('pages:tenants.billing.errors.cancelAddon'));
    } finally {
      setBillingSaving(false);
    }
  };

  const plansByType = useMemo(() => {
    const grouped: Record<SubscriptionPlanType, SubscriptionPlan[]> = {
      ai: [],
      reverse_ivr: [],
    };
    subscriptionPlans.forEach((plan) => {
      const type = (plan.planType || 'ai') as SubscriptionPlanType;
      grouped[type].push(plan);
    });
    return grouped;
  }, [subscriptionPlans]);

  const selectedPlan = useMemo(
    () => subscriptionPlans.find((plan) => plan.id === selectedPlanId) || null,
    [selectedPlanId, subscriptionPlans]
  );
  const hasTenantPublicApiEntitlement = useMemo(
    () => tenantSubscriptions.some((subscription) => (
      subscription.status === 'active' && Boolean(subscription.enablePublicApi)
    )),
    [tenantSubscriptions]
  );

  const packsBySubscription = useMemo(
    () =>
      tenantMinutePacks.reduce<Record<string, MinutePack[]>>((acc, pack) => {
        const subscriptionId = pack.subscriptionId;
        if (!subscriptionId) {
          return acc;
        }
        if (!acc[subscriptionId]) {
          acc[subscriptionId] = [];
        }
        acc[subscriptionId].push(pack);
        return acc;
      }, {}),
    [tenantMinutePacks]
  );

  const addonsBySubscription = useMemo(
    () =>
      tenantAddons.reduce<Record<string, SubscriptionAddon[]>>((acc, addon) => {
        const subscriptionId = addon.subscriptionId;
        if (!subscriptionId) {
          return acc;
        }
        if (!acc[subscriptionId]) {
          acc[subscriptionId] = [];
        }
        acc[subscriptionId].push(addon);
        return acc;
      }, {}),
    [tenantAddons]
  );

  const usagePeriodsBySubscription = useMemo(
    () =>
      tenantUsagePeriods.reduce<Record<string, SubscriptionUsagePeriodSummary[]>>((acc, usagePeriod) => {
        const subscriptionId = usagePeriod.subscriptionId;
        if (!subscriptionId) {
          return acc;
        }
        if (!acc[subscriptionId]) {
          acc[subscriptionId] = [];
        }
        acc[subscriptionId].push(usagePeriod);
        return acc;
      }, {}),
    [tenantUsagePeriods]
  );

  const getEligiblePackCatalog = useCallback(
    (subscription: TenantSubscriptionAdminItem): MinutePackCatalog[] =>
      minutePackCatalog.filter((pack) =>
        subscription.planType === 'reverse_ivr' ? pack.type !== 'ai' : true
      ),
    [minutePackCatalog]
  );

  const getEligibleAddons = useCallback(
    (subscription: TenantSubscriptionAdminItem): Addon[] =>
      availableAddons.filter((addon) =>
        subscription.planType === 'reverse_ivr' ? addon.code !== 'ai_agent_extra' : true
      ),
    [availableAddons]
  );

  const headerTitle = isEditing
    ? formData.name || t('pages:tenants.editTitle')
    : t('pages:tenants.newTitle');

  const formTabs: ButtonTab[] = [
    {
      id: TENANT_FORM_TABS.GENERAL,
      label: t('pages:tenants.tabs.general'),
      icon: <InfoOutlined fontSize="small" />,
      content: (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            label={t('pages:tenants.name')}
            value={formData.name}
            onChange={(event) => setFormData({ ...formData, name: event.target.value })}
            required
          />
          <TextField
            label={t('pages:tenants.email')}
            type="email"
            value={formData.email}
            onChange={(event) => setFormData({ ...formData, email: event.target.value })}
            required
          />
          {canEditNotificationEmails && (
            <TextField
            label={
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                <Typography component="span">{t('pages:tenants.notificationEmails')}</Typography>
                  <Tooltip title={t('pages:tenants.notificationEmailsTooltip')} arrow>
                    <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                </Box>
              }
              value={notificationEmailsInput}
              onChange={(event) => handleNotificationEmailsChange(event.target.value)}
              onBlur={(event) => handleNotificationEmailsChange(event.target.value)}
              placeholder={t('pages:tenants.notificationEmailsPlaceholder')}
              helperText={notificationEmailsError || t('pages:tenants.notificationEmailsHelper')}
              error={Boolean(notificationEmailsError)}
              multiline
              minRows={2}
            />
          )}
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(event) => setFormData({ ...formData, isActive: event.target.checked })}
              />
            }
            label={t('pages:tenants.isActive')}
          />
        </Box>
      ),
    },
    {
      id: TENANT_FORM_TABS.REVERSE_IVR,
      label: t('pages:tenants.tabs.reverseIvr'),
      icon: <Dialpad fontSize="small" />,
      content: (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {ivrLimitsError && <Alert severity="error">{ivrLimitsError}</Alert>}
          <TextField
            label={
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                <Typography component="span">{t('pages:tenants.ivrMaxAudioSeconds')}</Typography>
                <Tooltip title={t('pages:tenants.ivrMaxAudioSecondsTooltip')} arrow>
                  <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Box>
            }
            type="number"
            value={formData.ivrMaxAudioSeconds}
            onChange={(event) => handleIvrLimitChange('ivrMaxAudioSeconds', event.target.value)}
            inputProps={{ min: 1, step: 1 }}
            required
            error={Boolean(ivrLimitsError)}
            helperText={t('pages:tenants.ivrMaxAudioSecondsHelper')}
          />
          <TextField
            label={
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                <Typography component="span">{t('pages:tenants.ivrMaxCallDurationSeconds')}</Typography>
                <Tooltip title={t('pages:tenants.ivrMaxCallDurationSecondsTooltip')} arrow>
                  <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Box>
            }
            type="number"
            value={formData.ivrMaxCallDurationSeconds}
            onChange={(event) => handleIvrLimitChange('ivrMaxCallDurationSeconds', event.target.value)}
            inputProps={{ min: 1, step: 1 }}
            required
            error={Boolean(ivrLimitsError)}
            helperText={t('pages:tenants.ivrMaxCallDurationSecondsHelper')}
          />
          <TextField
            label={
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                <Typography component="span">{t('pages:tenants.ivrMaxPostAudioWaitSeconds')}</Typography>
                <Tooltip title={t('pages:tenants.ivrMaxPostAudioWaitSecondsTooltip')} arrow>
                  <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Box>
            }
            type="number"
            value={formData.ivrMaxPostAudioWaitSeconds}
            onChange={(event) => handleIvrLimitChange('ivrMaxPostAudioWaitSeconds', event.target.value)}
            inputProps={{ min: 0, step: 1 }}
            required
            error={Boolean(ivrLimitsError)}
            helperText={t('pages:tenants.ivrMaxPostAudioWaitSecondsHelper')}
          />
        </Box>
      ),
    },
    {
      id: TENANT_FORM_TABS.AUDIO_TUNING,
      label: t('pages:tenants.tabs.audioTuning'),
      icon: <GraphicEq fontSize="small" />,
      content: (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {t('pages:tenants.audioTuningTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('pages:tenants.audioTuningDesc')}
          </Typography>
          {audioTuningError && <Alert severity="error">{audioTuningError}</Alert>}
          {user?.isSuperuser === true && <FormControlLabel
              control={<Switch checked={formData.diagnosticRecordingTracksEnabled} onChange={(_, checked) => setFormData({ ...formData, diagnosticRecordingTracksEnabled: checked })} />}
              label={<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                <Typography component="span">{t('pages:tenants.diagnosticRecordingTracks')}</Typography>
                <Tooltip title={t('pages:tenants.diagnosticRecordingTracksHelp')} arrow>
                  <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Box>}
            />}
          <FormControlLabel
            control={
              <Switch
                checked={formData.pstnDenoiseRxEnabled}
                onChange={(event) => {
                  setFormData({ ...formData, pstnDenoiseRxEnabled: event.target.checked });
                  setAudioTuningError(null);
                }}
              />
            }
            label={
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                <Typography component="span">{t('pages:tenants.pstnDenoiseRxEnabled')}</Typography>
                <Tooltip title={t('pages:tenants.pstnDenoiseRxEnabledTooltip')} arrow>
                  <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Box>
            }
          />
          <TextField
            label={
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                <Typography component="span">{t('pages:tenants.pstnRxVolumeGain')}</Typography>
                <Tooltip title={t('pages:tenants.pstnRxVolumeGainTooltip')} arrow>
                  <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Box>
            }
            type="number"
            value={formData.pstnRxVolumeGain}
            onChange={(event) => {
              const numericValue = Number(event.target.value);
              setFormData((prev) => ({
                ...prev,
                pstnRxVolumeGain: Number.isFinite(numericValue) ? numericValue : 0,
              }));
              setAudioTuningError(null);
            }}
            inputProps={{ min: -20, max: 20, step: 1 }}
            required
            error={Boolean(audioTuningError)}
            helperText={t('pages:tenants.pstnRxVolumeGainHelper')}
          />
        </Box>
      ),
    },
    {
      id: TENANT_FORM_TABS.OPERATING_HOURS,
      label: t('pages:tenants.tabs.operatingHours'),
      icon: <Schedule fontSize="small" />,
      content: (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {t('pages:tenants.operatingHoursTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('pages:tenants.operatingHoursDesc')}
          </Typography>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.autoOperatorReadinessGuardEnabled}
                    onChange={(event) => {
                      setFormData({
                        ...formData,
                        autoOperatorReadinessGuardEnabled: event.target.checked,
                      });
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    <ShieldOutlined fontSize="small" color="primary" />
                    <Typography component="span" fontWeight={700}>
                      {t('pages:tenants.operatorReadinessGuard.label')}
                    </Typography>
                    <Tooltip title={t('pages:tenants.operatorReadinessGuard.tooltip')} arrow>
                      <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                }
              />
              <Typography variant="body2" color="text.secondary">
                {t('pages:tenants.operatorReadinessGuard.description')}
              </Typography>
            </Stack>
          </Paper>
          {operatingHoursError && <Alert severity="error">{operatingHoursError}</Alert>}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            {DAYS_OF_WEEK.map((day) => {
              const dayConfig = formData.operatingHours[day];
              return (
                <Paper
                  key={day}
                  variant="outlined"
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                    bgcolor: dayConfig.enabled ? 'background.paper' : 'action.hover',
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={dayConfig.enabled}
                        onChange={() => handleOperatingHourToggle(day)}
                      />
                    }
                    label={
                      <Typography
                        fontWeight={600}
                        sx={{
                          minWidth: 110,
                          color: dayConfig.enabled ? 'text.primary' : 'text.disabled',
                        }}
                      >
                        {getDayLabel(day)}
                      </Typography>
                    }
                    sx={{ mr: 1 }}
                  />
                  {dayConfig.enabled ? (
                    <>
                      {dayConfig.allDay ? (
                        <Chip
                          size="small"
                          color="success"
                          variant="outlined"
                          label={t('pages:tenants.operatingHoursAllDayBadge')}
                        />
                      ) : (
                        <>
                          <TimePicker
                            label={t('pages:tenants.operatingHoursStart')}
                            value={dayConfig.start ? dayjs(`2000-01-01T${dayConfig.start}`) : null}
                            onChange={(value: Dayjs | null) => {
                              handleOperatingHourTimeChange(day, 'start', value ? value.format('HH:mm') : '');
                            }}
                            ampm={false}
                            slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                          />
                          <Typography color="text.secondary">—</Typography>
                          <TimePicker
                            label={t('pages:tenants.operatingHoursEnd')}
                            value={dayConfig.end ? dayjs(`2000-01-01T${dayConfig.end}`) : null}
                            onChange={(value: Dayjs | null) => {
                              handleOperatingHourTimeChange(day, 'end', value ? value.format('HH:mm') : '');
                            }}
                            ampm={false}
                            slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
                          />
                        </>
                      )}
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(dayConfig.allDay)}
                            onChange={() => handleOperatingHourAllDayToggle(day)}
                          />
                        }
                        label={t('pages:tenants.operatingHoursAllDay')}
                      />
                    </>
                  ) : (
                    <Typography color="text.disabled" sx={{ fontStyle: 'italic' }}>
                      {t('pages:tenants.operatingHoursClosed')}
                    </Typography>
                  )}
                </Paper>
              );
            })}
          </LocalizationProvider>
        </Box>
      ),
    },
    {
      id: TENANT_FORM_TABS.MUSIC_ON_HOLD,
      label: t('pages:tenants.tabs.musicOnHold'),
      icon: <QueueMusic fontSize="small" />,
      content: (
        <Stack spacing={2}>
          <Typography variant="subtitle1" fontWeight={700}>
            {t('pages:tenants.musicOnHold.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('pages:tenants.musicOnHold.description')}
          </Typography>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2">{t('pages:tenants.musicOnHold.queueWait.title')}</Typography>
                {hasMusicFile('queueWait') && (
                  <Chip
                    size="small"
                    color={getMusicStatusTone(musicStatus.queueWaitMusicPrepared, musicStatus.queueWaitMusicError)}
                    label={getMusicStatusLabel('queueWait')}
                    sx={{ width: 'fit-content' }}
                  />
                )}
              </Box>
              <WaveformPlayerField
                url={musicMedia.queueWait?.downloadUrl}
                displayName={musicMedia.queueWait?.originalFilename}
                downloadFilename={musicMedia.queueWait?.originalFilename}
                uploadEnabled={isEditing}
                isUploading={uploadingMusicSlot === 'queueWait'}
                uploadProgress={musicUploadProgress.queueWait}
                disabled={!isEditing}
                onSelectFile={(selectedFile) => handleMusicFileUpload('queueWait', selectedFile)}
                onRemove={() => handleMusicFileRemove('queueWait')}
                emptyLabel={t('pages:tenants.musicOnHold.queueWait.empty')}
              />
              {musicMedia.queueWait && (
                <Stack spacing={1} sx={{ pt: 1 }}>
                  <Typography variant="body2">
                    {t('pages:tenants.musicOnHold.originalFile')}: {musicMedia.queueWait.originalFilename}
                  </Typography>
                  <Typography variant="body2">
                    {t('pages:tenants.musicOnHold.mimeType')}: {musicMedia.queueWait.mimeType}
                  </Typography>
                  <Typography variant="body2">
                    {t('pages:tenants.musicOnHold.fileSize')}: {formatFileSize(musicMedia.queueWait.fileSize)}
                  </Typography>
                </Stack>
              )}
              {hasMusicFile('queueWait') && musicStatus.queueWaitMusicError && (
                <Alert severity="error">{musicStatus.queueWaitMusicError}</Alert>
              )}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2">{t('pages:tenants.musicOnHold.agentHold.title')}</Typography>
                {hasMusicFile('agentHold') && (
                  <Chip
                    size="small"
                    color={getMusicStatusTone(musicStatus.agentHoldMusicPrepared, musicStatus.agentHoldMusicError)}
                    label={getMusicStatusLabel('agentHold')}
                    sx={{ width: 'fit-content' }}
                  />
                )}
              </Box>
              <WaveformPlayerField
                url={musicMedia.agentHold?.downloadUrl}
                displayName={musicMedia.agentHold?.originalFilename}
                downloadFilename={musicMedia.agentHold?.originalFilename}
                uploadEnabled={isEditing}
                isUploading={uploadingMusicSlot === 'agentHold'}
                uploadProgress={musicUploadProgress.agentHold}
                disabled={!isEditing}
                onSelectFile={(selectedFile) => handleMusicFileUpload('agentHold', selectedFile)}
                onRemove={() => handleMusicFileRemove('agentHold')}
                emptyLabel={t('pages:tenants.musicOnHold.agentHold.empty')}
              />
              {musicMedia.agentHold && (
                <Stack spacing={1} sx={{ pt: 1 }}>
                  <Typography variant="body2">
                    {t('pages:tenants.musicOnHold.originalFile')}: {musicMedia.agentHold.originalFilename}
                  </Typography>
                  <Typography variant="body2">
                    {t('pages:tenants.musicOnHold.mimeType')}: {musicMedia.agentHold.mimeType}
                  </Typography>
                  <Typography variant="body2">
                    {t('pages:tenants.musicOnHold.fileSize')}: {formatFileSize(musicMedia.agentHold.fileSize)}
                  </Typography>
                </Stack>
              )}
              {hasMusicFile('agentHold') && musicStatus.agentHoldMusicError && (
                <Alert severity="error">{musicStatus.agentHoldMusicError}</Alert>
              )}
            </Stack>
          </Paper>
        </Stack>
      ),
    },
    {
      id: TENANT_FORM_TABS.SUBSCRIPTIONS,
      label: t('pages:tenants.tabs.subscriptions'),
      icon: <SubscriptionsIcon fontSize="small" />,
      disabled: !isEditing,
      content: (
        <Stack spacing={2}>
          <Typography variant="subtitle1" fontWeight={700}>
            {t('pages:tenants.billing.subscriptions.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('pages:tenants.billing.subscriptions.description')}
          </Typography>
          {!isEditing ? (
            <Alert severity="info">{t('pages:tenants.billing.saveBeforeBilling')}</Alert>
          ) : (
            <>
              {billingLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={22} />
                </Box>
              )}
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t('pages:tenants.billing.subscriptions.selectPlan')}</InputLabel>
                  <Select
                    value={selectedPlanId}
                    label={t('pages:tenants.billing.subscriptions.selectPlan')}
                    onChange={(event: SelectChangeEvent) => setSelectedPlanId(event.target.value)}
                  >
                    {plansByType.ai.length > 0 && (
                      <ListSubheader>{t('pages:tenants.billing.subscriptions.planType.ai')}</ListSubheader>
                    )}
                    {plansByType.ai.map((plan) => (
                      <MenuItem key={plan.id} value={plan.id}>
                        {plan.name}
                        {' · '}
                        {formatCurrency(Number(plan.priceAmount), plan.priceCurrency, 2)}
                      </MenuItem>
                    ))}
                    {plansByType.reverse_ivr.length > 0 && (
                      <ListSubheader>{t('pages:tenants.billing.subscriptions.planType.reverseIvr')}</ListSubheader>
                    )}
                    {plansByType.reverse_ivr.map((plan) => (
                      <MenuItem key={plan.id} value={plan.id}>
                        {plan.name}
                        {' · '}
                        {formatCurrency(Number(plan.priceAmount), plan.priceCurrency, 2)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  type="number"
                  label={t('pages:tenants.billing.subscriptions.fields.priceOverride')}
                  value={selectedPlanPriceOverride}
                  onChange={(event) => setSelectedPlanPriceOverride(event.target.value)}
                  helperText={
                    selectedPlan
                      ? t('pages:tenants.billing.subscriptions.fields.priceOverrideHelper', {
                          catalogPrice: formatCurrency(
                            Number(selectedPlan.priceAmount),
                            selectedPlan.priceCurrency,
                            2
                          ),
                        })
                      : ' '
                  }
                />
                <Button
                  variant="contained"
                  disabled={!selectedPlanId || billingSaving}
                  onClick={handleAssignSubscription}
                >
                  {t('pages:tenants.billing.subscriptions.assignAction')}
                </Button>
              </Stack>
              {tenantSubscriptions.length === 0 ? (
                <Alert severity="info">{t('pages:tenants.billing.subscriptions.empty')}</Alert>
              ) : (
                <Stack spacing={1.5}>
                  {tenantSubscriptions.map((subscription) => {
                    const isSubscriptionActive = subscription.status === 'active';
                    const subscriptionPacks = packsBySubscription[subscription.id] || [];
                    const subscriptionAddons = addonsBySubscription[subscription.id] || [];
                    const subscriptionUsagePeriods = usagePeriodsBySubscription[subscription.id] || [];
                    const eligiblePacks = getEligiblePackCatalog(subscription);
                    const eligibleAddons = getEligibleAddons(subscription);
                    const selectedPack = eligiblePacks.find(
                      (pack) => pack.id === (selectedPackBySubscription[subscription.id] || '')
                    );
                    const selectedAddon = eligibleAddons.find(
                      (addon) => addon.id === (selectedAddonBySubscription[subscription.id] || '')
                    );

                    return (
                      <Accordion key={subscription.id} disableGutters>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={1}
                            alignItems={{ xs: 'flex-start', md: 'center' }}
                            justifyContent="space-between"
                            sx={{ width: '100%', pr: 1 }}
                          >
                            <Typography fontWeight={700}>{subscription.planName}</Typography>
                            <Stack direction="row" spacing={0.75}>
                              <Chip
                                size="small"
                                variant="outlined"
                                label={
                                  subscription.planType === 'reverse_ivr'
                                    ? t('pages:tenants.billing.subscriptions.planType.reverseIvr')
                                    : t('pages:tenants.billing.subscriptions.planType.ai')
                                }
                              />
                              <Chip
                                size="small"
                                color={isSubscriptionActive ? 'success' : 'default'}
                                label={t(`pages:tenants.billing.subscriptions.status.${subscription.status}`)}
                              />
                            </Stack>
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Stack spacing={2}>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                              <Typography variant="body2" color="text.secondary">
                                {t('pages:tenants.billing.subscriptions.columns.period')}:&nbsp;
                                {subscription.currentPeriodStart
                                  ? `${new Date(subscription.currentPeriodStart).toLocaleDateString()} - ${
                                      subscription.currentPeriodEnd
                                        ? new Date(subscription.currentPeriodEnd).toLocaleDateString()
                                        : '—'
                                    }`
                                  : '—'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {t('pages:tenants.billing.subscriptions.columns.price')}:&nbsp;
                                {formatCurrency(
                                  Number(subscription.planPriceAmount),
                                  subscription.planPriceCurrency,
                                  2
                                )}
                                {subscription.planPriceAmountOverride != null && (
                                  <>
                                    {' · '}
                                    {t('pages:tenants.billing.subscriptions.columns.catalogPrice')}:&nbsp;
                                    {formatCurrency(
                                      Number(subscription.planCatalogPriceAmount),
                                      subscription.planPriceCurrency,
                                      2
                                    )}
                                  </>
                                )}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {t('pages:tenants.billing.subscriptions.columns.overage')}:&nbsp;
                                {formatDurationValue(
                                  (subscription.overageAiSeconds || 0) +
                                    (subscription.overageTelephonySeconds || 0)
                                )}
                              </Typography>
                            </Stack>
                            {!isSubscriptionActive && (
                              <Alert severity="info">
                                {t('pages:tenants.billing.subscriptions.attachDisabledInactive')}
                              </Alert>
                            )}
                            <SubscriptionUsagePeriodsTable
                              title={t('pages:subscription.history.title')}
                              subtitle={t('pages:subscription.history.tenantSubtitle')}
                              usagePeriods={subscriptionUsagePeriods}
                              loading={billingLoading}
                              emptyMessage={t('pages:subscription.history.empty')}
                              asCard={false}
                            />
                            <Box
                              sx={{
                                display: 'grid',
                                gap: 2,
                                gridTemplateColumns: {
                                  xs: '1fr',
                                  lg: 'repeat(2, minmax(0, 1fr))',
                                },
                              }}
                            >
                              <Paper variant="outlined" sx={{ p: 2 }}>
                                <Stack spacing={1.5}>
                                  <Typography variant="subtitle2" fontWeight={700}>
                                    {t('pages:tenants.billing.packs.assignedTitle')}
                                  </Typography>
                                  {subscriptionPacks.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                      {t('pages:tenants.billing.packs.empty')}
                                    </Typography>
                                  ) : (
                                    <Stack spacing={1}>
                                      {subscriptionPacks.map((pack) => (
                                        <Paper key={pack.id} variant="outlined" sx={{ p: 1.25 }}>
                                          <Stack spacing={0.5}>
                                            <Typography variant="body2" fontWeight={600}>
                                              {pack.type === 'telephony'
                                                ? t('pages:tenants.billing.packs.type.telephony')
                                                : t('pages:tenants.billing.packs.type.ai')}
                                              {' · '}
                                              {pack.minutesQuantity}m
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              {t('pages:tenants.billing.packs.columns.remaining')}:&nbsp;
                                              {formatDurationValue(pack.secondsRemaining)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              {t('pages:tenants.billing.packs.columns.price')}:&nbsp;
                                              {formatCurrency(Number(pack.priceAmount), pack.priceCurrency, 2)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              {t('pages:tenants.billing.packs.columns.expires')}:&nbsp;
                                              {pack.expiresAt
                                                ? new Date(pack.expiresAt).toLocaleDateString()
                                                : '—'}
                                            </Typography>
                                          </Stack>
                                        </Paper>
                                      ))}
                                    </Stack>
                                  )}
                                  <Divider />
                                  {eligiblePacks.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                      {t('pages:tenants.billing.packs.catalogEmpty')}
                                    </Typography>
                                  ) : (
                                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                                      <FormControl fullWidth size="small">
                                        <InputLabel>{t('pages:tenants.billing.packs.fields.catalogPack')}</InputLabel>
                                        <Select
                                          value={selectedPackBySubscription[subscription.id] || ''}
                                          label={t('pages:tenants.billing.packs.fields.catalogPack')}
                                          onChange={(event: SelectChangeEvent) =>
                                            setSelectedPackBySubscription((prev) => ({
                                              ...prev,
                                              [subscription.id]: event.target.value,
                                            }))
                                          }
                                          disabled={!isSubscriptionActive || billingSaving}
                                        >
                                          {eligiblePacks.map((pack) => (
                                            <MenuItem key={pack.id} value={pack.id}>
                                              {pack.type === 'telephony'
                                                ? t('pages:tenants.billing.packs.type.telephony')
                                                : t('pages:tenants.billing.packs.type.ai')}
                                              {' · '}
                                              {pack.minutesQuantity}m
                                              {' · '}
                                              {formatCurrency(Number(pack.priceAmount), pack.priceCurrency, 2)}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                      <TextField
                                        size="small"
                                        type="number"
                                        label={t('pages:tenants.billing.packs.fields.price')}
                                        value={packPriceAmountBySubscription[subscription.id] || ''}
                                        onChange={(event) =>
                                          setPackPriceAmountBySubscription((prev) => ({
                                            ...prev,
                                            [subscription.id]: event.target.value,
                                          }))
                                        }
                                        disabled={!isSubscriptionActive || billingSaving}
                                        helperText={
                                          selectedPack
                                            ? t('pages:tenants.billing.packs.fields.priceHelper', {
                                                catalogPrice: formatCurrency(
                                                  Number(selectedPack.priceAmount),
                                                  selectedPack.priceCurrency,
                                                  2
                                                ),
                                              })
                                            : ' '
                                        }
                                      />
                                      <Button
                                        variant="contained"
                                        disabled={
                                          !isSubscriptionActive ||
                                          billingSaving ||
                                          !(selectedPackBySubscription[subscription.id] || '')
                                        }
                                        onClick={() => handleAttachPack(subscription)}
                                      >
                                        {t('pages:tenants.billing.packs.attachAction')}
                                      </Button>
                                    </Stack>
                                  )}
                                </Stack>
                              </Paper>

                              <Paper variant="outlined" sx={{ p: 2 }}>
                                <Stack spacing={1.5}>
                                  <Typography variant="subtitle2" fontWeight={700}>
                                    {t('pages:tenants.billing.addons.assignedTitle')}
                                  </Typography>
                                  {subscriptionAddons.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                      {t('pages:tenants.billing.addons.empty')}
                                    </Typography>
                                  ) : (
                                    <Stack spacing={1}>
                                      {subscriptionAddons.map((addon) => (
                                        <Paper key={addon.id} variant="outlined" sx={{ p: 1.25 }}>
                                          <Stack spacing={0.75}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                              <Typography variant="body2" fontWeight={600}>
                                                {addon.addon?.name || '-'}
                                              </Typography>
                                              <Chip
                                                size="small"
                                                color={addon.status === 'active' ? 'success' : 'default'}
                                                label={t(`pages:tenants.billing.addons.status.${addon.status}`)}
                                              />
                                            </Stack>
                                            <Typography variant="caption" color="text.secondary">
                                              {t('pages:tenants.billing.addons.columns.quantity')}: {addon.quantity}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              {t('pages:tenants.billing.addons.columns.price')}:&nbsp;
                                              {formatCurrency(Number(addon.priceAmount), addon.priceCurrency, 2)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              {t('pages:tenants.billing.addons.columns.endsAt')}:&nbsp;
                                              {addon.endsAt
                                                ? new Date(addon.endsAt).toLocaleDateString()
                                                : '—'}
                                            </Typography>
                                            {addon.status === 'active' && (
                                              <Box>
                                                <Button
                                                  size="small"
                                                  color="warning"
                                                  disabled={billingSaving}
                                                  onClick={() => handleCancelAddon(addon.id)}
                                                >
                                                  {t('pages:tenants.billing.addons.cancelAction')}
                                                </Button>
                                              </Box>
                                            )}
                                          </Stack>
                                        </Paper>
                                      ))}
                                    </Stack>
                                  )}
                                  <Divider />
                                  {eligibleAddons.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                      {t('pages:tenants.billing.addons.catalogEmpty')}
                                    </Typography>
                                  ) : (
                                    <Stack spacing={1.25}>
                                      <FormControl fullWidth size="small">
                                        <InputLabel>{t('pages:tenants.billing.addons.fields.addon')}</InputLabel>
                                        <Select
                                          value={selectedAddonBySubscription[subscription.id] || ''}
                                          label={t('pages:tenants.billing.addons.fields.addon')}
                                          onChange={(event: SelectChangeEvent) =>
                                            setSelectedAddonBySubscription((prev) => ({
                                              ...prev,
                                              [subscription.id]: event.target.value,
                                            }))
                                          }
                                          disabled={!isSubscriptionActive || billingSaving}
                                        >
                                          {eligibleAddons.map((addon) => (
                                            <MenuItem key={addon.id} value={addon.id}>
                                              {addon.name}
                                              {' · '}
                                              {formatCurrency(Number(addon.priceAmount), addon.priceCurrency, 2)}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                                        <TextField
                                          size="small"
                                          type="number"
                                          label={t('pages:tenants.billing.addons.fields.quantity')}
                                          value={addonQuantityBySubscription[subscription.id] || '1'}
                                          onChange={(event) =>
                                            setAddonQuantityBySubscription((prev) => ({
                                              ...prev,
                                              [subscription.id]: event.target.value,
                                            }))
                                          }
                                          inputProps={{ min: 1, step: 1 }}
                                          disabled={!isSubscriptionActive || billingSaving}
                                        />
                                        <TextField
                                          size="small"
                                          type="number"
                                          label={t('pages:tenants.billing.addons.fields.price')}
                                          value={addonPriceAmountBySubscription[subscription.id] || ''}
                                          onChange={(event) =>
                                            setAddonPriceAmountBySubscription((prev) => ({
                                              ...prev,
                                              [subscription.id]: event.target.value,
                                            }))
                                          }
                                          disabled={!isSubscriptionActive || billingSaving}
                                          helperText={
                                            selectedAddon
                                              ? t('pages:tenants.billing.addons.fields.priceHelper', {
                                                  catalogPrice: formatCurrency(
                                                    Number(selectedAddon.priceAmount),
                                                    selectedAddon.priceCurrency,
                                                    2
                                                  ),
                                                })
                                              : ' '
                                          }
                                        />
                                        <FormControlLabel
                                          control={
                                            <Switch
                                              checked={addonAutoRenewBySubscription[subscription.id] ?? true}
                                              onChange={(event) =>
                                                setAddonAutoRenewBySubscription((prev) => ({
                                                  ...prev,
                                                  [subscription.id]: event.target.checked,
                                                }))
                                              }
                                              disabled={!isSubscriptionActive || billingSaving}
                                            />
                                          }
                                          label={t('pages:tenants.billing.addons.fields.autoRenew')}
                                        />
                                      </Stack>
                                      <Box>
                                        <Button
                                          variant="contained"
                                          disabled={
                                            !isSubscriptionActive ||
                                            billingSaving ||
                                            !(selectedAddonBySubscription[subscription.id] || '')
                                          }
                                          onClick={() => handleAttachAddon(subscription)}
                                        >
                                          {t('pages:tenants.billing.addons.attachAction')}
                                        </Button>
                                      </Box>
                                    </Stack>
                                  )}
                                </Stack>
                              </Paper>
                            </Box>
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Stack>
              )}
            </>
          )}
        </Stack>
      ),
    },
    ...(canReadPublicApiCredentials && hasTenantPublicApiEntitlement
      ? [{
          id: TENANT_FORM_TABS.PUBLIC_API,
          label: t('pages:tenants.tabs.publicApi'),
          icon: <VpnKey fontSize="small" />,
          disabled: !isEditing,
          content: <PublicApiCredentialsManager mode="tenant" tenantId={id} />,
        }]
      : []),
  ];

  const headerActions = (
    <Button
      variant="contained"
      startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
      onClick={() => handleSubmit()}
      disabled={saving}
    >
      {saving ? t('common:actions.saving') : t('common:actions.save')}
    </Button>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <DetailHeader
        title={headerTitle}
        sectionIcon={<Business sx={{ fontSize: 32, color: 'primary.main' }} />}
        entityId={tenantId}
        breadcrumbs={breadcrumbs}
        actions={headerActions}
        backHref="/tenants"
      />

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <ButtonTabs
              tabs={formTabs}
              value={activeTab}
              onChange={(_, next) => setActiveTab(next as TenantFormTab)}
              tabsSx={{ mt: 0, '& .MuiTabs-flexContainer': { gap: 1 } }}
              tabButtonSx={{ px: 1.5, py: 0.4, minHeight: 36 }}
              contentSx={{ mt: 3 }}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
